import { useState } from "react";
import { Link } from "react-router-dom";
import { AMIR_COIN, shortMint } from "@/lib/coin";
import { Copy, Check, ExternalLink, Users, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function CoinCard() {
  const [copied, setCopied] = useState(false);

  const copyMint = async () => {
    try {
      await navigator.clipboard.writeText(AMIR_COIN.mint);
      setCopied(true);
      toast.success("Contract address copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  return (
    <div data-testid="coin-card" className="bg-white border border-[#6A0DAD] rounded-2xl p-7 shadow-sm">
      <div className="flex items-center gap-4">
        <img
          src={AMIR_COIN.logo}
          alt="AMIR coin logo"
          className="w-16 h-16 rounded-full border-2 border-[#6A0DAD] object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <div>
          <div className="flex items-center gap-2">
            <h3 className="font-heading text-2xl font-bold text-[#1A202C]">{AMIR_COIN.symbol}</h3>
            <span className="px-2.5 py-0.5 rounded-full bg-[#6A0DAD]/10 text-[#6A0DAD] text-xs font-heading font-semibold">{AMIR_COIN.chain}</span>
          </div>
          <p className="text-sm text-[#4A5568] font-heading">A Miracle is Real — the official coin</p>
        </div>
      </div>

      <p className="text-[#4A5568] leading-relaxed mt-4 text-sm">{AMIR_COIN.description}</p>

      <div data-testid="coin-purpose" className="mt-5 rounded-2xl bg-[#6A0DAD]/8 border border-[#6A0DAD]/30 p-5">
        <p className="font-heading text-xs uppercase tracking-widest text-[#B8972E] mb-1.5">More than a meme</p>
        <p className="font-display text-xl md:text-2xl text-[#1A202C] leading-snug">
          AMIR is a <span className="text-[#6A0DAD] italic">purpose token</span>.
        </p>
        <p className="text-[#4A5568] leading-relaxed mt-2 text-sm">
          It's not just another coin — it stands for hope, resilience, and the NICU families behind it.
          Holding AMIR is how you say <span className="font-semibold text-[#1A202C]">you acknowledge the movement</span> and
          stand with these little fighters.
        </p>
      </div>

      <div className="mt-5">
        <p className="text-xs font-heading uppercase tracking-widest text-[#B8972E] mb-1.5">Contract address</p>
        <button
          data-testid="coin-copy-mint"
          onClick={copyMint}
          className="w-full flex items-center justify-between gap-2 bg-[#FDFBF7] border border-[#6A0DAD]/40 rounded-xl px-4 py-3 font-mono text-sm text-[#1A202C] hover:border-[#6A0DAD] transition-colors"
        >
          <span className="truncate">{AMIR_COIN.mint}</span>
          {copied ? <Check size={16} className="text-green-600 shrink-0" /> : <Copy size={16} className="text-[#6A0DAD] shrink-0" />}
        </button>
      </div>

      <div className="mt-5 flex flex-wrap gap-3">
        <a
          href={AMIR_COIN.pumpUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="coin-buy-btn"
          className="inline-flex items-center gap-2 bg-[#6A0DAD] text-white hover:bg-[#58098F] rounded-full px-6 py-3 font-heading font-semibold transition-colors"
        >
          Buy AMIR on pump.fun <ExternalLink size={16} />
        </a>
        <a
          href={AMIR_COIN.solscanUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="coin-solscan-btn"
          className="inline-flex items-center gap-2 bg-white border border-[#6A0DAD] text-[#6A0DAD] hover:shadow-md rounded-full px-6 py-3 font-heading font-semibold transition-all"
        >
          View on Solscan <ExternalLink size={16} />
        </a>
        <a
          href={AMIR_COIN.communityUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="coin-community-btn"
          className="inline-flex items-center gap-2 bg-white border border-[#6A0DAD] text-[#6A0DAD] hover:shadow-md rounded-full px-6 py-3 font-heading font-semibold transition-all"
        >
          <Users size={16} /> Community
        </a>
      </div>

      <Link
        to="/token"
        data-testid="coin-transparency-link"
        className="mt-4 inline-flex items-center gap-2 text-[#6A0DAD] font-heading font-semibold text-sm hover:underline"
      >
        <ShieldCheck size={16} /> Token transparency — use cases, rules & verifiable flows
      </Link>
    </div>
  );
}
